import React, {useEffect, useMemo} from 'react';
import {VStack} from 'native-base';
import {DndContext, closestCenter, PointerSensor, useSensor, useSensors} from '@dnd-kit/core';
import {arrayMove, SortableContext, verticalListSortingStrategy} from '@dnd-kit/sortable';
import uuid from 'react-native-uuid';
import {t} from '../../../../../../services/i18n';
import CustomButtonV2 from '../../../../../common/CustomButtonV2';
import {Colors} from '../../../../../../styles/Colors';
import UpsellRow from './UpsellRow';

const UpsellSelection = (props) => {
    const {value, onChange, options, currencySymbol, errors} = props;
    const sensors = useSensors(useSensor(PointerSensor));
    const defaultEmptyRow = useMemo(() => ({membership_types_id: null, price: null, order: 0, originalPrice: null, randomKey: uuid.v4()}), []);

    const emptyRow = (order = 0) => ({membership_types_id: null, price: null, order, originalPrice: null, randomKey: uuid.v4()});
    const rows = useMemo(() => value?.length ? value : [defaultEmptyRow], [value, defaultEmptyRow]);

    useEffect(() => {
        if (!value?.length) {
            onChange([defaultEmptyRow]);
            return;
        }
        // Hydrated rows from the server have no randomKey — add one for DnD/React keys
        if (value.some(row => !row.randomKey)) {
            onChange(value.map(row => row.randomKey ? row : {...row, randomKey: uuid.v4()}));
        }
    }, [value?.length]);

    const selectedProductIds = useMemo(() => rows.map(row => row.membership_types_id).filter(id => id != null), [rows]);

    const updateRows = (nextRows) => {
        onChange(nextRows.map((row, index) => ({...row, order: index})));
    };

    const onProductChange = (index, productId) => {
        const product = (options ?? []).find(opt => opt.id === productId);
        updateRows(rows.map((row, i) => {
            if (i !== index) return row;
            return {...row, membership_types_id: productId, originalPrice: product ? Number(product.price) : null, price: productId == null ? null : row.price};
        }));
    };

    const onPriceChange = (index, price) => {
        updateRows(rows.map((row, i) => i === index ? {...row, price: price === '' ? null : price} : row));
    };

    // Only row left → reset empty; otherwise remove and renumber
    const onDelete = (index) => {
        if (rows.length === 1) {
            updateRows([emptyRow(0)]);
            return;
        }
        updateRows(rows.filter((_, i) => i !== index));
    };

    const onAddItem = () => updateRows([...rows, emptyRow(rows.length)]);

    const onDragEnd = (event) => {
        const {active, over} = event;
        if (!over || active.id === over.id) return;
        const from = rows.findIndex(row => row.randomKey === active.id);
        const to = rows.findIndex(row => row.randomKey === over.id);
        if (from < 0 || to < 0) return;
        updateRows(arrayMove(rows, from, to));
    };

    const getRowError = (index) => {
        if (!errors) return null;
        if (typeof errors === 'string') return index === 0 ? errors : null;
        const rowError = errors[index];
        if (!rowError) return null;
        return rowError.price || rowError.membership_types_id || (typeof rowError === 'string' ? rowError : null);
    };

    const optionsForRow = (row) => (options ?? []).map(opt => ({
        ...opt,
        disabled: selectedProductIds.includes(opt.id) && opt.id !== row.membership_types_id,
    }));

    return (
        <VStack space={'0.75rem'} flex={1}>
            <DndContext sensors={sensors} onDragEnd={onDragEnd} collisionDetection={closestCenter}>
                <SortableContext items={rows.map(row => row.randomKey)} strategy={verticalListSortingStrategy}>
                    {rows.map((row, index) => (
                        <UpsellRow key={row.randomKey} row={row} index={index} productOptions={optionsForRow(row)}
                                   currencySymbol={currencySymbol} onProductChange={onProductChange}
                                   onPriceChange={onPriceChange} onDelete={onDelete} error={getRowError(index)}/>
                    ))}
                </SortableContext>
            </DndContext>
            <CustomButtonV2 type={'link'} text={t('upsell-add-item')} onClick={onAddItem}
                            wrapperStyle={{alignSelf: 'flex-start'}}
                            textProps={{color: Colors.newDesign.mainBlue}}
                            iconProps={{iconColor: Colors.newDesign.mainBlue}}/>
        </VStack>
    );
};

export default UpsellSelection;
